// Weekly Billing — admin-facing UI for the Friday cycle.
//
// Workflow: pick a week → review what would be billed/paid → Generate
// (persists drafts + renders PDFs) → Send (emails recipient, flips to sent)
// → Mark Paid (logs to billing_transactions for invoices) → optional Void.
//
// Wired into BillingManagement.js as the "Weekly Cycle" sub-tab.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardBody, CardHeader, Heading, HStack, VStack, Text, Button,
  Input, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Badge,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Flex, Alert,
  AlertIcon, Tooltip, useToast, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, useDisclosure, FormControl,
  FormLabel, Select, Textarea,
} from '@chakra-ui/react';
import { FaCalendarWeek, FaFlask, FaUserNurse, FaChevronLeft, FaChevronRight, FaInfoCircle, FaSave, FaTrashAlt, FaFilePdf, FaPaperPlane, FaCheckCircle, FaBan } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { renderLabInvoicePdf, renderPhlebPayslipPdf } from '../../utils/billingPdf';
import { getAuthHeaders } from '../../utils/apiAuth';

const PDF_BUCKET = 'billing-pdfs';

// ── Date helpers (all in browser-local time; finance team works ET; revisit if cross-timezone matters) ──
//
// IMPORTANT: day arithmetic uses setDate(), not getTime() ± N*86400000.
// Millisecond math loses an hour on DST transitions and lands on the wrong
// day for one week per year — startOfDay() then snaps to the WRONG midnight.

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// Most recent Sunday that is in the past (the "week ending" anchor).
function defaultWeekEnding(today = new Date()) {
  const t = startOfDay(today);
  const dow = t.getDay(); // 0=Sun
  // If today is Sunday, the most recent ENDED week ended last Sunday (7 days ago).
  const offset = dow === 0 ? 7 : dow;
  return addDays(t, -offset);
}

function mondayBefore(sunday) {
  return startOfDay(addDays(sunday, -6));
}

function fmtDate(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtShort(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
}

function toISODate(d) {
  const x = startOfDay(d);
  return x.toISOString().slice(0, 10);
}

// ── Billable-draw rule ──
// A submission is billable / payable for a given week when:
//   • Phleb logged a successful-style outcome (successful_draw, partial_draw,
//     difficult_stick_completed), OR — if no outcome — status moved past the draw
//     (waiting_to_be_received, waiting_on_lab_results, completed).
//   • outcome_logged_at (fallback blood_collection_time, then submitted_at) falls
//     in the Mon-Sun window.
//
// Weekly invoicing replaces batch invoicing — batch_id on a submission is
// purely informational now (we don't filter on it).

const BILLABLE_OUTCOMES = new Set(['successful_draw', 'partial_draw', 'difficult_stick_completed']);
const POST_DRAW_STATUSES = new Set(['waiting_to_be_received', 'waiting_on_lab_results', 'completed']);

function isBillable(s) {
  const outcome = (s.outcome_code || '').toLowerCase();
  if (outcome) return BILLABLE_OUTCOMES.has(outcome);
  return POST_DRAW_STATUSES.has((s.status || '').toLowerCase());
}

function drawDate(s) {
  return s.outcome_logged_at || s.blood_collection_time || s.submitted_at || null;
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Snap to 2 decimals before persisting — keeps the DB free of float drift like
// 12.499999998. Use NUMERIC's storage precision authoritatively at write time.
function round2(n) {
  const v = Number(n || 0);
  return Math.round(v * 100) / 100;
}

// Summary chips for what's already persisted this week: 2 draft · 1 sent.
// Empty if nothing has been generated yet.
function ExistingBadges({ rows }) {
  if (!rows || rows.length === 0) return null;
  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const colorByStatus = { draft: 'gray', sent: 'blue', paid: 'green', void: 'red' };
  return (
    <HStack spacing={1}>
      {['draft', 'sent', 'paid', 'void'].filter((s) => counts[s]).map((s) => (
        <Badge key={s} size="sm" colorScheme={colorByStatus[s]} variant="subtle">{counts[s]} {s}</Badge>
      ))}
    </HStack>
  );
}

export default function WeeklyBillingPreview() {
  const toast = useToast();
  const [weekEnding, setWeekEnding] = useState(() => defaultWeekEnding());
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [phlebsById, setPhlebsById] = useState({});
  const [labsById, setLabsById] = useState({});

  // Existing weekly_lab_invoices / weekly_phleb_payslips for THIS week_ending.
  // Drives the generate buttons (block if any are sent/paid, allow re-gen of drafts).
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [existingPayslips, setExistingPayslips] = useState([]);
  const [working, setWorking] = useState(null); // 'invoices' | 'payslips' | null

  const monday = useMemo(() => mondayBefore(weekEnding), [weekEnding]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const startIso = startOfDay(monday).toISOString();
        const endIso   = endOfDay(weekEnding).toISOString();

        // Pull submissions whose outcome was logged OR whose collection-time / submitted-at
        // falls in the window. Over-fetch is fine; we re-filter client-side via isBillable.
        // We OR three date fields with or() Supabase syntax.
        const { data: subs, error } = await supabase
          .from('submissions')
          .select('id, draw_code, patient_name, status, outcome_code, outcome_logged_at, blood_collection_time, submitted_at, lab_id, phlebotomist_id, admin_fee, draw_fee_paid')
          .or(`outcome_logged_at.gte.${startIso},blood_collection_time.gte.${startIso},submitted_at.gte.${startIso}`)
          .lte('submitted_at', endIso)
          .order('submitted_at', { ascending: false })
          .limit(5000);
        if (error) throw error;

        const filtered = (subs || []).filter((s) => {
          if (!isBillable(s)) return false;
          const dt = drawDate(s);
          if (!dt) return false;
          const t = new Date(dt).getTime();
          return t >= startOfDay(monday).getTime() && t <= endOfDay(weekEnding).getTime();
        });

        // Hydrate phlebs (rate + name) and labs (name) for everything referenced.
        const phlebIds = [...new Set(filtered.map((s) => s.phlebotomist_id).filter(Boolean))];
        const labIds   = [...new Set(filtered.map((s) => s.lab_id).filter(Boolean))];

        let pMap = {};
        if (phlebIds.length > 0) {
          // phleb_pay_rate may not exist yet (added in add_weekly_billing.sql).
          // Try the wider select first, fall back to legacy columns if Postgres
          // rejects the unknown column — keeps the preview usable pre-migration.
          let phlebs = null;
          const tryWith = await supabase
            .from('phlebotomist_profiles')
            .select('id, full_name, company_name, email, lab_draw_fee, phleb_pay_rate')
            .in('id', phlebIds);
          if (tryWith.error) {
            const fallback = await supabase
              .from('phlebotomist_profiles')
              .select('id, full_name, company_name, email, lab_draw_fee')
              .in('id', phlebIds);
            phlebs = fallback.data;
          } else {
            phlebs = tryWith.data;
          }
          (phlebs || []).forEach((p) => { pMap[p.id] = p; });
        }
        let lMap = {};
        if (labIds.length > 0) {
          const { data: labs } = await supabase
            .from('labs')
            .select('id, name')
            .in('id', labIds);
          (labs || []).forEach((l) => { lMap[l.id] = l; });
        }

        if (cancelled) return;
        setRows(filtered);
        setPhlebsById(pMap);
        setLabsById(lMap);
      } catch (e) {
        console.error('WeeklyBillingPreview fetch failed:', e);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [monday, weekEnding]);

  // ── Aggregate ──
  // For each row, derive:
  //   _drawFee  — what the lab is billed (phleb.lab_draw_fee snapshot)
  //   _payRate  — what the phleb is paid (phleb.phleb_pay_rate, falls back to
  //               lab_draw_fee when the column hasn't been backfilled / set yet)
  //   _adminFee — fee already on the submission
  //   _margin   — _drawFee - _payRate
  const enriched = useMemo(() => rows.map((s) => {
    const phleb = phlebsById[s.phlebotomist_id];
    const lab = labsById[s.lab_id];
    const drawFee = phleb?.lab_draw_fee ? Number(phleb.lab_draw_fee) : 0;
    const adminFee = s.admin_fee ? Number(s.admin_fee) : 0;
    const payRate = phleb?.phleb_pay_rate != null
      ? Number(phleb.phleb_pay_rate)
      : drawFee;
    return {
      ...s,
      _phleb: phleb || null,
      _lab: lab || null,
      _drawDate: drawDate(s),
      _drawFee: drawFee,
      _adminFee: adminFee,
      _payRate: payRate,
      _margin: drawFee - payRate,
      _cashSettled: !!s.draw_fee_paid,
    };
  }), [rows, phlebsById, labsById]);

  const labBuckets = useMemo(() => {
    const map = new Map();
    enriched.forEach((r) => {
      if (!r.lab_id) return;
      const k = r.lab_id;
      const bucket = map.get(k) || { lab: r._lab, lab_id: k, lines: [], subtotal: 0, adminTotal: 0 };
      // Skip cash-settled rows in the lab subtotal (already billed elsewhere).
      const lineTotal = r._cashSettled ? 0 : r._drawFee + r._adminFee;
      bucket.lines.push(r);
      bucket.subtotal += r._cashSettled ? 0 : r._drawFee;
      bucket.adminTotal += r._cashSettled ? 0 : r._adminFee;
      bucket.total = (bucket.total || 0) + lineTotal;
      map.set(k, bucket);
    });
    return [...map.values()].sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [enriched]);

  const phlebBuckets = useMemo(() => {
    const map = new Map();
    enriched.forEach((r) => {
      if (!r.phlebotomist_id) return;
      const k = r.phlebotomist_id;
      const bucket = map.get(k) || { phleb: r._phleb, phleb_id: k, lines: [], gross: 0, cashAlreadyPaid: 0 };
      bucket.lines.push(r);
      // Cash-settled rows: phleb already got the money, don't owe again on payslip.
      if (r._cashSettled) bucket.cashAlreadyPaid += r._payRate;
      else                bucket.gross += r._payRate;
      map.set(k, bucket);
    });
    return [...map.values()].sort((a, b) => (b.gross || 0) - (a.gross || 0));
  }, [enriched]);

  const stats = useMemo(() => {
    const cashSettled = enriched.filter((r) => r._cashSettled).length;
    // Draws with no phlebotomist_id — billed to the lab (still has admin fee)
    // but generate no payslip because there's no one to pay. Surfacing this
    // count helps admin spot draws that need a phleb assigned in Blood Files
    // before the invoice goes out.
    const unassigned = enriched.filter((r) => !r.phlebotomist_id).length;
    return {
      draws: enriched.length,
      labTotal: labBuckets.reduce((s, b) => s + (b.total || 0), 0),
      phlebTotal: phlebBuckets.reduce((s, b) => s + (b.gross || 0), 0),
      cashSettled,
      unassigned,
    };
  }, [enriched, labBuckets, phlebBuckets]);

  const shiftWeek = (deltaDays) => setWeekEnding((prev) => addDays(prev, deltaDays));

  // ── Existing drafts / sent / paid rows for this week ──────────────────────
  // Refetched whenever the week changes or after a successful generate.
  const refreshExisting = useCallback(async () => {
    const weekIso = toISODate(weekEnding);
    const [{ data: invs }, { data: payslips }] = await Promise.all([
      supabase.from('weekly_lab_invoices')
        .select('id, lab_id, week_ending, status, total, generated_at, pdf_url')
        .eq('week_ending', weekIso),
      supabase.from('weekly_phleb_payslips')
        .select('id, phlebotomist_id, week_ending, status, net_pay, generated_at, pdf_url')
        .eq('week_ending', weekIso),
    ]);
    setExistingInvoices(invs || []);
    setExistingPayslips(payslips || []);
  }, [weekEnding]);

  useEffect(() => { refreshExisting(); }, [refreshExisting]);

  const invoiceLock = useMemo(() => existingInvoices.find((i) => i.status !== 'draft'), [existingInvoices]);
  const payslipLock = useMemo(() => existingPayslips.find((p) => p.status !== 'draft'), [existingPayslips]);

  // ── Generate lab invoices ────────────────────────────────────────────────
  // Inserts one row per labBucket, then a line per submission inside that
  // bucket. Re-runs delete existing DRAFTS first; sent/paid rows block the run.
  const generateInvoices = async () => {
    if (invoiceLock) {
      toast({
        title: 'Cannot regenerate',
        description: `An invoice for this week is already ${invoiceLock.status}. Void it first if you need to redo.`,
        status: 'error', duration: 5000, isClosable: true,
      });
      return;
    }
    if (labBuckets.length === 0) {
      toast({ title: 'Nothing to generate', description: 'No billable draws for this week.', status: 'warning', duration: 3000 });
      return;
    }
    setWorking('invoices');
    try {
      // Wipe any prior drafts for this week so re-gen starts clean.
      const draftIds = existingInvoices.filter((i) => i.status === 'draft').map((i) => i.id);
      if (draftIds.length > 0) {
        const { error } = await supabase.from('weekly_lab_invoices').delete().in('id', draftIds);
        if (error) throw error;
      }

      const weekIso = toISODate(weekEnding);
      const parents = labBuckets.map((b) => ({
        lab_id: b.lab_id,
        week_ending: weekIso,
        subtotal_draws: round2(b.subtotal),
        admin_fee_total: round2(b.adminTotal),
        ship_fee_total: 0,
        total: round2(b.total),
        status: 'draft',
      }));

      const { data: insertedInvoices, error: pErr } = await supabase
        .from('weekly_lab_invoices')
        .insert(parents)
        .select('id, lab_id');
      if (pErr) throw pErr;

      const invoiceIdByLab = Object.fromEntries(insertedInvoices.map((r) => [r.lab_id, r.id]));
      const lines = labBuckets.flatMap((b) =>
        b.lines.map((l) => ({
          invoice_id: invoiceIdByLab[b.lab_id],
          submission_id: l.id,
          phlebotomist_id: l.phlebotomist_id || null,
          patient_name: l.patient_name || null,
          draw_date: l._drawDate,
          draw_fee: round2(l._cashSettled ? 0 : l._drawFee),
          admin_fee: round2(l._cashSettled ? 0 : l._adminFee),
          line_total: round2(l._cashSettled ? 0 : l._drawFee + l._adminFee),
        }))
      );

      if (lines.length > 0) {
        const { error: lErr } = await supabase.from('weekly_lab_invoice_lines').insert(lines);
        if (lErr) throw lErr;
      }

      // Render + upload PDFs in parallel. Stamp pdf_url onto each row.
      // Failures here are logged but don't roll back the DB rows — the data
      // is the source of truth, the PDF is a derived artifact we can re-render.
      const pdfResults = await Promise.allSettled(labBuckets.map(async (b) => {
        const invoice = insertedInvoices.find((i) => i.lab_id === b.lab_id);
        if (!invoice) return;
        const pdfInvoice = {
          id: invoice.id,
          week_ending: weekIso,
          generated_at: new Date().toISOString(),
          subtotal_draws: round2(b.subtotal),
          admin_fee_total: round2(b.adminTotal),
          ship_fee_total: 0,
          total: round2(b.total),
          invoice_number: null,
        };
        const pdfLines = b.lines.map((l) => ({
          draw_date: l._drawDate,
          patient_name: l.patient_name,
          phleb_name: l._phleb?.full_name || l._phleb?.company_name || null,
          draw_fee: l._cashSettled ? 0 : l._drawFee,
          admin_fee: l._cashSettled ? 0 : l._adminFee,
          line_total: l._cashSettled ? 0 : (l._drawFee + l._adminFee),
        }));
        const blob = renderLabInvoicePdf({ invoice: pdfInvoice, lines: pdfLines, lab: b.lab });
        const path = `invoices/${b.lab_id}/${weekIso}.pdf`;
        const { error: upErr } = await supabase.storage
          .from(PDF_BUCKET)
          .upload(path, blob, { contentType: 'application/pdf', upsert: true });
        if (upErr) throw upErr;
        const { error: updErr } = await supabase
          .from('weekly_lab_invoices')
          .update({ pdf_url: path })
          .eq('id', invoice.id);
        if (updErr) throw updErr;
      }));
      const pdfFailures = pdfResults.filter((r) => r.status === 'rejected');
      if (pdfFailures.length > 0) console.warn('Some invoice PDFs failed:', pdfFailures.map((f) => f.reason));

      toast({
        title: `Generated ${parents.length} invoice draft${parents.length === 1 ? '' : 's'}`,
        description: `${lines.length} line${lines.length === 1 ? '' : 's'} across ${parents.length} lab${parents.length === 1 ? '' : 's'}. Total ${fmtMoney(parents.reduce((s, p) => s + p.total, 0))}.${pdfFailures.length > 0 ? ` ${pdfFailures.length} PDF render(s) failed — rows saved.` : ''}`,
        status: pdfFailures.length > 0 ? 'warning' : 'success', duration: 5000, isClosable: true,
      });
      await refreshExisting();
    } catch (e) {
      console.error('generateInvoices failed:', e);
      toast({ title: 'Generate failed', description: e.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setWorking(null);
    }
  };

  // ── Generate phleb payslips ──────────────────────────────────────────────
  const generatePayslips = async () => {
    if (payslipLock) {
      toast({
        title: 'Cannot regenerate',
        description: `A payslip for this week is already ${payslipLock.status}. Void it first if you need to redo.`,
        status: 'error', duration: 5000, isClosable: true,
      });
      return;
    }
    if (phlebBuckets.length === 0) {
      toast({ title: 'Nothing to generate', description: 'No payable draws for this week.', status: 'warning', duration: 3000 });
      return;
    }
    setWorking('payslips');
    try {
      const draftIds = existingPayslips.filter((p) => p.status === 'draft').map((p) => p.id);
      if (draftIds.length > 0) {
        const { error } = await supabase.from('weekly_phleb_payslips').delete().in('id', draftIds);
        if (error) throw error;
      }

      const weekIso = toISODate(weekEnding);
      // Only insert payslips where there's actual money owed (gross > 0).
      const owingBuckets = phlebBuckets.filter((b) => b.gross > 0);
      if (owingBuckets.length === 0) {
        toast({ title: 'Nothing to pay', description: 'All draws this week were cash-settled.', status: 'info', duration: 3000 });
        setWorking(null);
        return;
      }

      const parents = owingBuckets.map((b) => ({
        phlebotomist_id: b.phleb_id,
        week_ending: weekIso,
        draws_count: b.lines.filter((l) => !l._cashSettled).length,
        gross_pay: round2(b.gross),
        adjustments_total: 0,
        net_pay: round2(b.gross),
        status: 'draft',
      }));

      const { data: insertedPayslips, error: pErr } = await supabase
        .from('weekly_phleb_payslips')
        .insert(parents)
        .select('id, phlebotomist_id');
      if (pErr) throw pErr;

      const payslipIdByPhleb = Object.fromEntries(insertedPayslips.map((r) => [r.phlebotomist_id, r.id]));
      const lines = owingBuckets.flatMap((b) =>
        b.lines
          .filter((l) => !l._cashSettled)  // cash-settled never lands on a payslip
          .map((l) => ({
            payslip_id: payslipIdByPhleb[b.phleb_id],
            submission_id: l.id,
            lab_id: l.lab_id || null,
            patient_name: l.patient_name || null,
            draw_date: l._drawDate,
            pay_rate: round2(l._payRate),
            is_adjustment: false,
            adjustment_reason: null,
            amount: round2(l._payRate),
          }))
      );

      if (lines.length > 0) {
        const { error: lErr } = await supabase.from('weekly_phleb_payslip_lines').insert(lines);
        if (lErr) throw lErr;
      }

      const pdfResults = await Promise.allSettled(owingBuckets.map(async (b) => {
        const payslip = insertedPayslips.find((p) => p.phlebotomist_id === b.phleb_id);
        if (!payslip) return;
        const pdfPayslip = {
          id: payslip.id,
          week_ending: weekIso,
          generated_at: new Date().toISOString(),
          draws_count: b.lines.filter((l) => !l._cashSettled).length,
          gross_pay: round2(b.gross),
          adjustments_total: 0,
          net_pay: round2(b.gross),
          payslip_number: null,
        };
        const pdfLines = b.lines
          .filter((l) => !l._cashSettled)
          .map((l) => ({
            draw_date: l._drawDate,
            patient_name: l.patient_name,
            lab_name: l._lab?.name || null,
            pay_rate: l._payRate,
            amount: l._payRate,
            is_adjustment: false,
          }));
        const blob = renderPhlebPayslipPdf({ payslip: pdfPayslip, lines: pdfLines, phleb: b.phleb });
        const path = `payslips/${b.phleb_id}/${weekIso}.pdf`;
        const { error: upErr } = await supabase.storage
          .from(PDF_BUCKET)
          .upload(path, blob, { contentType: 'application/pdf', upsert: true });
        if (upErr) throw upErr;
        const { error: updErr } = await supabase
          .from('weekly_phleb_payslips')
          .update({ pdf_url: path })
          .eq('id', payslip.id);
        if (updErr) throw updErr;
      }));
      const pdfFailures = pdfResults.filter((r) => r.status === 'rejected');
      if (pdfFailures.length > 0) console.warn('Some payslip PDFs failed:', pdfFailures.map((f) => f.reason));

      toast({
        title: `Generated ${parents.length} payslip draft${parents.length === 1 ? '' : 's'}`,
        description: `${lines.length} line${lines.length === 1 ? '' : 's'} across ${parents.length} phleb${parents.length === 1 ? '' : 's'}. Owed ${fmtMoney(parents.reduce((s, p) => s + p.net_pay, 0))}.${pdfFailures.length > 0 ? ` ${pdfFailures.length} PDF render(s) failed — rows saved.` : ''}`,
        status: pdfFailures.length > 0 ? 'warning' : 'success', duration: 5000, isClosable: true,
      });
      await refreshExisting();
    } catch (e) {
      console.error('generatePayslips failed:', e);
      toast({ title: 'Generate failed', description: e.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setWorking(null);
    }
  };

  // ── Send a draft row ────────────────────────────────────────────────────
  // POSTs to /api/send-billing-doc which: emails the recipient with the PDF
  // attached, flips status to 'sent', stamps sent_at. UI refreshes after.
  const [sendingId, setSendingId] = useState(null);
  const sendDoc = async (kind, id) => {
    setSendingId(id);
    try {
      const res = await fetch('/api/send-billing-doc', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ kind, id }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.error) {
        toast({
          title: 'Send failed',
          description: json.error || `HTTP ${res.status}`,
          status: 'error', duration: 6000, isClosable: true,
        });
        return;
      }
      toast({
        title: 'Sent',
        description: `Delivered to ${json.sentTo}.`,
        status: 'success', duration: 4000, isClosable: true,
      });
      await refreshExisting();
    } catch (e) {
      toast({ title: 'Send failed', description: e.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSendingId(null);
    }
  };

  // Mint a short-lived signed URL and open the PDF in a new tab. The pdf_url
  // column stores the storage path (NOT a public URL) so we can keep the bucket
  // private and gate access here.
  const openPdf = async (path) => {
    if (!path) return;
    const { data, error } = await supabase.storage
      .from(PDF_BUCKET)
      .createSignedUrl(path, 60 * 60);
    if (error || !data?.signedUrl) {
      toast({ title: 'PDF unavailable', description: error?.message || 'Could not generate download link.', status: 'error', duration: 4000 });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  // Look up the persisted row for a bucket so we can show its status + PDF link.
  const invoiceForLab = useCallback(
    (labId) => existingInvoices.find((i) => i.lab_id === labId) || null,
    [existingInvoices]
  );
  const payslipForPhleb = useCallback(
    (phlebId) => existingPayslips.find((p) => p.phlebotomist_id === phlebId) || null,
    [existingPayslips]
  );

  // ── Mark a sent row as paid ──────────────────────────────────────────────
  // Opens the modal. For invoices we also write a billing_transactions ledger
  // row so the lab's payment shows up in the existing Transactions tab.
  const { isOpen: payOpen, onOpen: payModalOpen, onClose: payClose } = useDisclosure();
  const [payTarget, setPayTarget] = useState(null); // { kind, row }
  const [payForm, setPayForm] = useState({ paid_via: '', paid_reference: '', paid_at: '', notes: '' });

  const openMarkPaid = (kind, row) => {
    setPayTarget({ kind, row });
    setPayForm({
      paid_via: kind === 'invoice' ? 'stripe' : 'ach',
      paid_reference: '',
      paid_at: new Date().toISOString().slice(0, 10),
      notes: '',
    });
    payModalOpen();
  };

  const submitMarkPaid = async () => {
    if (!payTarget) return;
    const { kind, row } = payTarget;
    if (!payForm.paid_via) {
      toast({ title: 'Pick a payment method', status: 'warning', duration: 3000 });
      return;
    }
    setWorking('mark-paid');
    try {
      const table = kind === 'invoice' ? 'weekly_lab_invoices' : 'weekly_phleb_payslips';
      const paidAtIso = new Date(payForm.paid_at + 'T12:00:00').toISOString();
      // Lost-the-race guard: UPDATE returns the rows it touched via .select().
      // If another admin already marked this paid, the eq('status','sent') no
      // longer matches and we get an empty data array — abort BEFORE inserting
      // a duplicate billing_transactions row.
      const { data: updatedRows, error: updErr } = await supabase
        .from(table)
        .update({
          status: 'paid',
          paid_at: paidAtIso,
          paid_via: payForm.paid_via,
          paid_reference: payForm.paid_reference || null,
          // Overwrites the notes column. Only sent when the admin typed
          // something so we don't clobber a prior note with empty string.
          ...(payForm.notes ? { notes: payForm.notes } : {}),
        })
        .eq('id', row.id)
        .eq('status', 'sent')
        .select('id');
      if (updErr) throw updErr;
      if (!updatedRows || updatedRows.length === 0) {
        toast({
          title: 'Already updated',
          description: 'Another admin marked this row paid, or its status changed. Refreshed.',
          status: 'warning', duration: 4000, isClosable: true,
        });
        await refreshExisting();
        payClose();
        return;
      }

      // For lab invoices, mirror the payment into billing_transactions so the
      // existing Transactions tab and downstream reporting both see it.
      if (kind === 'invoice') {
        const { error: txErr } = await supabase.from('billing_transactions').insert({
          lab_id: row.lab_id,
          amount: row.total,
          currency: 'USD',
          status: 'completed',
          type: 'one_time',
          description: `Weekly invoice for week ending ${row.week_ending}`,
          stripe_transaction_id: payForm.paid_via === 'stripe' ? (payForm.paid_reference || null) : null,
          payment_method_type: payForm.paid_via,
          transaction_date: paidAtIso,
          metadata: { source: 'weekly_lab_invoices', weekly_invoice_id: row.id, week_ending: row.week_ending },
        });
        if (txErr) {
          // Ledger write failed but the invoice IS marked paid — surface both.
          toast({
            title: 'Marked paid, ledger write failed',
            description: txErr.message,
            status: 'warning', duration: 7000, isClosable: true,
          });
          await refreshExisting();
          payClose();
          return;
        }
      }

      toast({ title: 'Marked paid', status: 'success', duration: 3000 });
      await refreshExisting();
      payClose();
    } catch (e) {
      toast({ title: 'Mark paid failed', description: e.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setWorking(null);
    }
  };

  // ── Void a draft or sent row ─────────────────────────────────────────────
  // Status='void' preserves history. Lines stay attached. Admin can re-generate
  // afterwards because the unique (week, lab/phleb) constraint allows multiple
  // voids (we delete them explicitly on regenerate via the existing draft path,
  // but voided rows block too — admin must manually delete if they want to redo).
  const voidRow = async (kind, row) => {
    if (!window.confirm(`Void this ${kind}? It will be marked void and no longer counted. This is reversible only by deleting the row directly.`)) return;
    const table = kind === 'invoice' ? 'weekly_lab_invoices' : 'weekly_phleb_payslips';
    const { error } = await supabase.from(table).update({ status: 'void' }).eq('id', row.id);
    if (error) {
      toast({ title: 'Void failed', description: error.message, status: 'error', duration: 5000 });
      return;
    }
    toast({ title: 'Voided', status: 'info', duration: 2500 });
    await refreshExisting();
  };

  // ── Discard a set of drafts (the "redo from scratch" escape hatch) ──────
  const discardDrafts = async (kind /* 'invoices' | 'payslips' */) => {
    const table = kind === 'invoices' ? 'weekly_lab_invoices' : 'weekly_phleb_payslips';
    const list = kind === 'invoices' ? existingInvoices : existingPayslips;
    const draftIds = list.filter((x) => x.status === 'draft').map((x) => x.id);
    if (draftIds.length === 0) return;
    if (!window.confirm(`Delete ${draftIds.length} ${kind} draft${draftIds.length === 1 ? '' : 's'} for this week? Sent/paid rows are untouched.`)) return;
    const { error } = await supabase.from(table).delete().in('id', draftIds);
    if (error) {
      toast({ title: 'Delete failed', description: error.message, status: 'error', duration: 5000 });
      return;
    }
    toast({ title: `Discarded ${draftIds.length} draft${draftIds.length === 1 ? '' : 's'}`, status: 'info', duration: 2500 });
    await refreshExisting();
  };

  return (
    <Box>
      {/* Header / week picker */}
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
        <HStack>
          <FaCalendarWeek />
          <Heading size="md">Weekly Cycle</Heading>
          <Tooltip label="Pick a week, review the math, then Generate to save invoice/payslip drafts. Drafts can be regenerated; once status is sent or paid, the week locks.">
            <Box as="span" cursor="help"><FaInfoCircle color="#718096" /></Box>
          </Tooltip>
        </HStack>
        <HStack>
          <Button size="sm" leftIcon={<FaChevronLeft />} variant="outline" onClick={() => shiftWeek(-7)}>Prev week</Button>
          <Box>
            <Text fontSize="xs" color="gray.500" textAlign="center">Week ending</Text>
            <Input
              size="sm"
              type="date"
              value={toISODate(weekEnding)}
              onChange={(e) => setWeekEnding(startOfDay(new Date(e.target.value + 'T12:00:00')))}
            />
          </Box>
          <Button size="sm" rightIcon={<FaChevronRight />} variant="outline" onClick={() => shiftWeek(7)}>Next week</Button>
        </HStack>
      </Flex>

      <Text color="gray.600" mb={4} fontSize="sm">
        Covers <b>{fmtDate(monday)}</b> – <b>{fmtDate(weekEnding)}</b> (Mon–Sun, draw date).
      </Text>


      {/* Top stats */}
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={6}>
        <Stat bg="white" p={3} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Billable draws</StatLabel>
          <StatNumber color="blue.600">{stats.draws}</StatNumber>
          <StatHelpText fontSize="xs">
            {stats.cashSettled} cash-settled
            {stats.unassigned > 0 && (
              <>
                {' · '}
                <Text as="span" color="red.500" fontWeight="600">{stats.unassigned} unassigned</Text>
              </>
            )}
          </StatHelpText>
        </Stat>
        <Stat bg="white" p={3} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Owed by labs</StatLabel>
          <StatNumber color="green.600">{fmtMoney(stats.labTotal)}</StatNumber>
          <StatHelpText fontSize="xs">{labBuckets.length} invoice{labBuckets.length === 1 ? '' : 's'}</StatHelpText>
        </Stat>
        <Stat bg="white" p={3} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Owed to phlebs</StatLabel>
          <StatNumber color="purple.600">{fmtMoney(stats.phlebTotal)}</StatNumber>
          <StatHelpText fontSize="xs">{phlebBuckets.length} payslip{phlebBuckets.length === 1 ? '' : 's'}</StatHelpText>
        </Stat>
        <Stat bg="white" p={3} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Net margin</StatLabel>
          <StatNumber color={stats.labTotal - stats.phlebTotal >= 0 ? 'teal.600' : 'red.500'}>
            {fmtMoney(stats.labTotal - stats.phlebTotal)}
          </StatNumber>
          <StatHelpText fontSize="xs">Lab total − phleb total</StatHelpText>
        </Stat>
        <Stat bg="white" p={3} borderRadius="md" borderWidth="1px">
          <StatLabel fontSize="xs">Avg per draw</StatLabel>
          <StatNumber color="gray.700">{fmtMoney(stats.draws ? stats.labTotal / stats.draws : 0)}</StatNumber>
          <StatHelpText fontSize="xs">Lab-side</StatHelpText>
        </Stat>
      </SimpleGrid>

      {loading ? (
        <Flex justify="center" py={20}><Spinner size="lg" /></Flex>
      ) : enriched.length === 0 ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          No billable draws found for this week. Try Prev week, or check that successful-draw outcomes are being logged.
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="start">
          {/* ── Lab invoices column ── */}
          <Box>
            <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
              <HStack>
                <FaFlask color="#38A169" />
                <Heading size="sm">Lab invoices ({labBuckets.length})</Heading>
                <ExistingBadges rows={existingInvoices} />
              </HStack>
              <HStack>
                {existingInvoices.some((i) => i.status === 'draft') && (
                  <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<FaTrashAlt />} onClick={() => discardDrafts('invoices')}>
                    Discard drafts
                  </Button>
                )}
                <Button
                  size="sm"
                  colorScheme="green"
                  leftIcon={<FaSave />}
                  isLoading={working === 'invoices'}
                  isDisabled={!!invoiceLock || labBuckets.length === 0 || working !== null}
                  onClick={generateInvoices}
                >
                  {existingInvoices.some((i) => i.status === 'draft') ? 'Regenerate' : 'Generate'} invoices
                </Button>
              </HStack>
            </Flex>
            {invoiceLock && (
              <Alert status="warning" mb={3} borderRadius="md" fontSize="xs">
                <AlertIcon />
                Locked — invoice already <b>{invoiceLock.status}</b> for this week. Void it before regenerating.
              </Alert>
            )}
            <VStack spacing={3} align="stretch">
              {labBuckets.map((b) => {
                const inv = invoiceForLab(b.lab_id);
                return (
                <Card key={b.lab_id} variant="outline">
                  <CardHeader pb={2}>
                    <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                      <Box>
                        <HStack>
                          <Heading size="sm">{b.lab?.name || 'Unknown lab'}</Heading>
                          {inv && (
                            <Badge colorScheme={inv.status === 'draft' ? 'gray' : inv.status === 'sent' ? 'blue' : inv.status === 'paid' ? 'green' : 'red'}>
                              {inv.status}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.500">{b.lines.length} draw{b.lines.length === 1 ? '' : 's'}</Text>
                      </Box>
                      <HStack>
                        {inv?.pdf_url && (
                          <Button size="xs" leftIcon={<FaFilePdf />} variant="outline" colorScheme="green" onClick={() => openPdf(inv.pdf_url)}>
                            PDF
                          </Button>
                        )}
                        {inv?.status === 'draft' && inv?.pdf_url && (
                          <Button
                            size="xs"
                            leftIcon={<FaPaperPlane />}
                            colorScheme="blue"
                            isLoading={sendingId === inv.id}
                            isDisabled={sendingId !== null}
                            onClick={() => sendDoc('invoice', inv.id)}
                          >
                            Send
                          </Button>
                        )}
                        {inv?.status === 'sent' && (
                          <Button size="xs" leftIcon={<FaCheckCircle />} colorScheme="green" onClick={() => openMarkPaid('invoice', inv)}>
                            Mark paid
                          </Button>
                        )}
                        {inv && (inv.status === 'draft' || inv.status === 'sent') && (
                          <Tooltip label="Void this invoice">
                            <Button size="xs" leftIcon={<FaBan />} variant="ghost" colorScheme="red" onClick={() => voidRow('invoice', inv)}>
                              Void
                            </Button>
                          </Tooltip>
                        )}
                        <Box textAlign="right">
                          <Text fontSize="xs" color="gray.500">Total</Text>
                          <Heading size="md" color="green.600">{fmtMoney(b.total)}</Heading>
                        </Box>
                      </HStack>
                    </Flex>
                  </CardHeader>
                  <CardBody pt={0}>
                    <TableContainer>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th px={2}>Date</Th>
                            <Th px={2}>Patient</Th>
                            <Th px={2}>Phleb</Th>
                            <Th px={2} isNumeric>Draw</Th>
                            <Th px={2} isNumeric>Admin</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {b.lines.map((l) => (
                            <Tr key={l.id}>
                              <Td px={2} fontSize="xs">{fmtShort(l._drawDate)}</Td>
                              <Td px={2} fontSize="xs">
                                {l.patient_name}
                                {l._cashSettled && <Badge ml={1} size="xs" colorScheme="gray">cash</Badge>}
                              </Td>
                              <Td px={2} fontSize="xs">
                                {l._phleb?.full_name || (
                                  <Tooltip label="No phleb assigned on the submission — nobody will be paid for this draw. Assign a phleb in Blood Files first.">
                                    <Badge size="xs" colorScheme="red" variant="subtle">unassigned</Badge>
                                  </Tooltip>
                                )}
                              </Td>
                              <Td px={2} isNumeric fontSize="xs">{l._cashSettled ? '—' : fmtMoney(l._drawFee)}</Td>
                              <Td px={2} isNumeric fontSize="xs">{l._cashSettled ? '—' : fmtMoney(l._adminFee)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                    {(b.subtotal !== b.total) && (
                      <HStack justify="flex-end" mt={2} fontSize="xs" color="gray.600">
                        <Text>Subtotal {fmtMoney(b.subtotal)} · Admin {fmtMoney(b.adminTotal)}</Text>
                      </HStack>
                    )}
                  </CardBody>
                </Card>
                );
              })}
            </VStack>
          </Box>

          {/* ── Phleb payslips column ── */}
          <Box>
            <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
              <HStack>
                <FaUserNurse color="#805AD5" />
                <Heading size="sm">Phleb payslips ({phlebBuckets.length})</Heading>
                <ExistingBadges rows={existingPayslips} />
              </HStack>
              <HStack>
                {existingPayslips.some((p) => p.status === 'draft') && (
                  <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<FaTrashAlt />} onClick={() => discardDrafts('payslips')}>
                    Discard drafts
                  </Button>
                )}
                <Button
                  size="sm"
                  colorScheme="purple"
                  leftIcon={<FaSave />}
                  isLoading={working === 'payslips'}
                  isDisabled={!!payslipLock || phlebBuckets.length === 0 || working !== null}
                  onClick={generatePayslips}
                >
                  {existingPayslips.some((p) => p.status === 'draft') ? 'Regenerate' : 'Generate'} payslips
                </Button>
              </HStack>
            </Flex>
            {payslipLock && (
              <Alert status="warning" mb={3} borderRadius="md" fontSize="xs">
                <AlertIcon />
                Locked — payslip already <b>{payslipLock.status}</b> for this week. Void it before regenerating.
              </Alert>
            )}
            <VStack spacing={3} align="stretch">
              {phlebBuckets.map((b) => {
                const slip = payslipForPhleb(b.phleb_id);
                return (
                <Card key={b.phleb_id} variant="outline">
                  <CardHeader pb={2}>
                    <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                      <Box>
                        <HStack>
                          <Heading size="sm">{b.phleb?.full_name || 'Unknown phleb'}</Heading>
                          {slip && (
                            <Badge colorScheme={slip.status === 'draft' ? 'gray' : slip.status === 'sent' ? 'blue' : slip.status === 'paid' ? 'green' : 'red'}>
                              {slip.status}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {b.lines.length} draw{b.lines.length === 1 ? '' : 's'}
                          {b.cashAlreadyPaid > 0 && ` · ${fmtMoney(b.cashAlreadyPaid)} already paid in cash`}
                        </Text>
                      </Box>
                      <HStack>
                        {slip?.pdf_url && (
                          <Button size="xs" leftIcon={<FaFilePdf />} variant="outline" colorScheme="purple" onClick={() => openPdf(slip.pdf_url)}>
                            PDF
                          </Button>
                        )}
                        {slip?.status === 'draft' && slip?.pdf_url && (
                          <Button
                            size="xs"
                            leftIcon={<FaPaperPlane />}
                            colorScheme="blue"
                            isLoading={sendingId === slip.id}
                            isDisabled={sendingId !== null}
                            onClick={() => sendDoc('payslip', slip.id)}
                          >
                            Send
                          </Button>
                        )}
                        {slip?.status === 'sent' && (
                          <Button size="xs" leftIcon={<FaCheckCircle />} colorScheme="green" onClick={() => openMarkPaid('payslip', slip)}>
                            Mark paid
                          </Button>
                        )}
                        {slip && (slip.status === 'draft' || slip.status === 'sent') && (
                          <Tooltip label="Void this payslip">
                            <Button size="xs" leftIcon={<FaBan />} variant="ghost" colorScheme="red" onClick={() => voidRow('payslip', slip)}>
                              Void
                            </Button>
                          </Tooltip>
                        )}
                        <Box textAlign="right">
                          <Text fontSize="xs" color="gray.500">Owe</Text>
                          <Heading size="md" color="purple.600">{fmtMoney(b.gross)}</Heading>
                        </Box>
                      </HStack>
                    </Flex>
                  </CardHeader>
                  <CardBody pt={0}>
                    <TableContainer>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th px={2}>Date</Th>
                            <Th px={2}>Patient</Th>
                            <Th px={2}>Lab</Th>
                            <Th px={2} isNumeric>Pay</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {b.lines.map((l) => (
                            <Tr key={l.id}>
                              <Td px={2} fontSize="xs">{fmtShort(l._drawDate)}</Td>
                              <Td px={2} fontSize="xs">
                                {l.patient_name}
                                {l._cashSettled && <Badge ml={1} size="xs" colorScheme="gray">cash</Badge>}
                              </Td>
                              <Td px={2} fontSize="xs">{l._lab?.name || '—'}</Td>
                              <Td px={2} isNumeric fontSize="xs">{l._cashSettled ? '—' : fmtMoney(l._payRate)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </CardBody>
                </Card>
                );
              })}
            </VStack>
          </Box>
        </SimpleGrid>
      )}

      {/* ── Mark Paid modal ────────────────────────────────────────────── */}
      <Modal isOpen={payOpen} onClose={payClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Mark {payTarget?.kind === 'invoice' ? 'invoice' : 'payslip'} paid
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              {payTarget && (
                <Box bg="gray.50" borderRadius="md" p={3} fontSize="sm">
                  <Text><b>Week ending:</b> {payTarget.row.week_ending}</Text>
                  <Text>
                    <b>Amount:</b>{' '}
                    {fmtMoney(payTarget.kind === 'invoice' ? payTarget.row.total : payTarget.row.net_pay)}
                  </Text>
                </Box>
              )}
              <FormControl isRequired>
                <FormLabel fontSize="sm">Paid via</FormLabel>
                <Select
                  size="sm"
                  value={payForm.paid_via}
                  onChange={(e) => setPayForm((f) => ({ ...f, paid_via: e.target.value }))}
                >
                  {payTarget?.kind === 'invoice' ? (
                    <>
                      <option value="stripe">Stripe</option>
                      <option value="ach">ACH / Bank transfer</option>
                      <option value="wire">Wire</option>
                      <option value="check">Check</option>
                      <option value="other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="ach">ACH (default)</option>
                      <option value="zelle">Zelle</option>
                      <option value="check">Check</option>
                      <option value="cash">Cash</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Reference</FormLabel>
                <Input
                  size="sm"
                  placeholder={payTarget?.kind === 'invoice' ? 'Stripe charge ID, wire confirmation, etc.' : 'ACH trace #, Zelle confirmation, check #'}
                  value={payForm.paid_reference}
                  onChange={(e) => setPayForm((f) => ({ ...f, paid_reference: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Paid on</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={payForm.paid_at}
                  onChange={(e) => setPayForm((f) => ({ ...f, paid_at: e.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Notes (optional)</FormLabel>
                <Textarea
                  size="sm"
                  rows={2}
                  value={payForm.notes}
                  onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={payClose} size="sm">Cancel</Button>
            <Button
              colorScheme="green"
              size="sm"
              leftIcon={<FaCheckCircle />}
              isLoading={working === 'mark-paid'}
              onClick={submitMarkPaid}
            >
              Mark paid
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
